import { Link, Outlet, useLocation, Navigate, useNavigate } from 'react-router';
import { LayoutDashboard, PlusCircle, BarChart3, User, LogOut, CreditCard, Coins, ChevronDown, FileText, Shield } from 'lucide-react';
import { motion } from 'motion/react';
import { Logo } from './Logo';
import { useAuth } from '../contexts/AuthContext';
import { useState, useEffect, useRef } from 'react';
import { BuyCreditsModal } from './BuyCreditsModal';

export function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, credits, loading, signOut } = useAuth();
  const [isBuyCreditsModalOpen, setBuyCreditsModalOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isUserMenuOpen]);
  
  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2 }}
          className="text-center"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
            className="w-12 h-12 border-3 border-[#4F819F] border-t-transparent rounded-full mx-auto mb-3"
          ></motion.div>
          <p className="text-slate-600 text-sm">Loading your workspace...</p>
        </motion.div>
      </div>
    );
  }
  
  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  const isActive = (path: string) => {
    return location.pathname === path;
  };
  
  const handleSignOut = async () => {
    setIsUserMenuOpen(false);
    await signOut();
  };
  
  const handleNavigate = (path: string) => {
    setIsUserMenuOpen(false);
    navigate(path);
  };
  
  const handleBuyCredits = () => {
    setIsUserMenuOpen(false);
    setBuyCreditsModalOpen(true);
  };
  
  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden bg-slate-50">
      {/* Animated background elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-20 right-20 w-96 h-96 bg-[#4F819F]/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-[#4E7F9D]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-[#4F819F]/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>
      
      {/* Header with glassmorphism */}
      <motion.header 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/60 shadow-lg shadow-slate-200/50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3 group">
              <motion.div
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              >
                <Logo className="h-12 w-auto" />
              </motion.div>
            </Link>
            
            <div className="flex items-center gap-6">
              <nav className="flex items-center gap-3">
                <NavLink to="/" icon={LayoutDashboard} active={isActive('/')}>
                  Dashboard
                </NavLink>
              </nav>
              
              <div className="h-8 w-px bg-slate-300"></div>
              
              {/* User Menu */}
              <div ref={menuRef} className="relative">
                <button
                  className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-slate-100 transition-all outline-none focus-visible:ring-2 focus-visible:ring-[#4F819F]/30"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#4F819F] to-[#4E7F9D] flex items-center justify-center text-white font-semibold text-sm">
                    {user.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-slate-900">{user.name || user.email?.split('@')[0] || 'User'}</p>
                    <p className="text-xs text-slate-500">{credits?.balance || 0} Credits Available</p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-slate-600 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white border border-slate-200/60 shadow-xl rounded-xl overflow-hidden z-50">
                    <div className="p-3 border-b border-slate-100">
                      <p className="font-medium text-sm text-slate-900">{user.name || user.email?.split('@')[0] || 'User'}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                    <div className="py-1">
                      <button
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                        onClick={() => handleNavigate('/profile-settings')}
                      >
                        <User className="w-4 h-4" />
                        Profile Settings
                      </button>
                      <button
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                        onClick={() => handleNavigate('/billing')}
                      >
                        <FileText className="w-4 h-4" />
                        Billing & Invoices
                      </button>
                      {(user.isAdmin || user.isSuperAdmin) && (
                        <button
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-700 hover:bg-red-50 transition-colors border-t border-slate-100"
                          onClick={() => handleNavigate('/admin')}
                        >
                          <Shield className="w-4 h-4" />
                          Admin Panel
                        </button>
                      )}
                      {!user.isAdmin && !user.isSuperAdmin && (
                        <button
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                          onClick={handleBuyCredits}
                        >
                          <Coins className="w-4 h-4" />
                          Buy Credits
                        </button>
                      )}
                    </div>
                    <div className="border-t border-slate-100">
                      <button
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        onClick={handleSignOut}
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.header>
      
      {/* Main Content - flex-1 ensures it takes remaining space */}
      <main className="relative flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12">
        <Outlet />
      </main>
      
      {/* Footer with glassmorphism - always at bottom */}
      <footer className="relative backdrop-blur-xl bg-white/80 border-t border-slate-200/60 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col items-center justify-center space-y-2 text-center">
            <p className="text-sm text-slate-600">
              © 2026 CRRSoft, Inc. All rights reserved. HelioROI is a trademark of CRRSoft, Inc.
            </p>
            <p className="text-xs text-slate-500">
              7901 4TH ST N STE 300, ST. PETERSBURG, FL 33702, United States
            </p>
            <p className="text-xs text-slate-500">
              <a href="tel:+17273902400" className="hover:text-slate-700">+1 (727) 390-2400</a>
              {' • '}
              <a href="mailto:info@helioroi.com" className="hover:text-slate-700">info@helioroi.com</a>
            </p>
          </div>
        </div>
      </footer>
      
      {/* Buy Credits Modal */}
      <BuyCreditsModal isOpen={isBuyCreditsModalOpen} onClose={() => setBuyCreditsModalOpen(false)} />
    </div>
  );
}

interface NavLinkProps {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  active: boolean;
  children: React.ReactNode;
}

function NavLink({ to, icon: Icon, active, children }: NavLinkProps) {
  return (
    <Link to={to}>
      <motion.div
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={`
          flex items-center gap-2 px-5 py-2.5 rounded-xl transition-all relative overflow-hidden
          ${active 
            ? 'bg-[#4F819F]/15 text-[#41474A] shadow-md shadow-[#4F819F]/20' 
            : 'text-slate-600 hover:text-[#41474A] hover:bg-slate-100/80'
          }
        `}
      >
        {active && (
          <motion.div
            layoutId="activeNav"
            className="absolute inset-0 bg-[#4F819F]/10 rounded-xl border border-[#4F819F]/30"
            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
          />
        )}
        <Icon className="w-4 h-4 relative z-10" />
        <span className="text-sm font-medium relative z-10">{children}</span>
      </motion.div>
    </Link>
  );
}